import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import AudioPlayer, { AudioPlayerRef } from '../components/learning/AudioPlayer';
import placementTestService, {
    BackgroundQuestion,
    BackgroundAnswer,
    TestQuestion,
    CEFRProbabilities,
    TestResult,
    CEFRLevel,
    DictationQuestion,
    DictationScoreResult,
    SpeakingQuestion,
    SpeakingScoreResult,
} from '../services/placementTestService';
import { CEFRGuideModal } from '../components/cefr';
import QuestionBreakdownSection from '../components/QuestionBreakdownSection';
import RecommendedCoursesSection from '../components/RecommendedCoursesSection';
import LetterPicker from '../components/placement-test/LetterPicker';
import { usePhonicsErrorCollector } from '../hooks/usePhonicsErrorCollector';
import { usePhonicsGuidance } from '../hooks/usePhonicsGuidance';
import PhonicsSessionSummary from '../components/learning/PhonicsSessionSummary';
import InputModeModal, { DictationInputMode } from '../components/placement-test/InputModeModal';
import PreferenceTooltip from '../components/placement-test/PreferenceTooltip';
import { containsFullWidthAlpha, toHalfWidth } from '../utils/wordComparison';
import { useToastStore } from '../stores/toastStore';
import './PlacementTestPage.css';

// Test phases
type TestPhase = 'intro' | 'background' | 'testing' | 'listening-dictation' | 'speaking' | 'login-prompt' | 'result';

const PlacementTestPage: React.FC = () => {
    const { t, i18n } = useTranslation('placementTest');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, user } = useAuth();
    const locale = i18n.language;

    // Keyboard height detection for mobile (prevents page jumping)
    useKeyboardHeight();

    /** Resolve a localized text map to the best available string for the current locale */
    const localized = (map: Record<string, string>): string =>
        map[locale] || map[locale.split('-')[0]] || map['en'] || '';

    // Check if user has a token (apiClient handles refresh automatically)
    const hasToken = () => !!localStorage.getItem('accessToken');


    // Test state
    const [phase, setPhase] = useState<TestPhase>('intro');
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [backgroundQuestions, setBackgroundQuestions] = useState<BackgroundQuestion[]>([]);
    const [backgroundAnswers, setBackgroundAnswers] = useState<BackgroundAnswer[]>([]);
    const [currentBgIndex, setCurrentBgIndex] = useState(0);
    const [multiSelectChoices, setMultiSelectChoices] = useState<Set<number>>(new Set());

    // Testing phase state
    const [currentQuestion, setCurrentQuestion] = useState<TestQuestion | null>(null);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [, setProbabilities] = useState<CEFRProbabilities | null>(null);
    const [questionStartTime, setQuestionStartTime] = useState<number>(0);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);

    // Listening dictation state
    const [dictationQuestions, setDictationQuestions] = useState<DictationQuestion[]>([]);
    const [currentDictationIndex, setCurrentDictationIndex] = useState(0);
    const [dictationStartTime, setDictationStartTime] = useState(0);
    const [lastDictationScore, setLastDictationScore] = useState<DictationScoreResult | null>(null);

    // Word-by-word dictation input state
    const [dictationWordInputs, setDictationWordInputs] = useState<string[]>([]);
    const [activeDictationWordIndex, setActiveDictationWordIndex] = useState(0);
    const dictationInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const dictationAudioRef = useRef<AudioPlayerRef>(null);
    const fullWidthWarningRef = useRef<number>(0);

    // Speaking state
    const [speakingQuestions, setSpeakingQuestions] = useState<SpeakingQuestion[]>([]);
    const [currentSpeakingIndex, setCurrentSpeakingIndex] = useState(0);
    const {
        isRecording,
        audioBlob: speakingAudioBlob,
        startRecording,
        stopRecording,
        clearRecording: clearSpeakingRecording,
        error: recordingError,
    } = useAudioRecorder();
    const [lastSpeakingScore, setLastSpeakingScore] = useState<SpeakingScoreResult | null>(null);

    // Result state
    const [result, setResult] = useState<TestResult | null>(null);
    const [preview, setPreview] = useState<{ message: string; teaser: string } | null>(null);
    const [showBreakdown, setShowBreakdown] = useState(false);

    // Loading/error state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // CEFR info modal state
    const [showCefrModal, setShowCefrModal] = useState(false);

    // Dictation input mode state — prefer user profile, fallback to localStorage
    const [dictationInputMode, setDictationInputMode] = useState<DictationInputMode>(() => {
        return user?.dictationInputMode
            || (localStorage.getItem('pt-dictation-input-mode') as DictationInputMode)
            || '';
    });
    const [showInputModeModal, setShowInputModeModal] = useState(false);
    const [showPreferenceTooltip, setShowPreferenceTooltip] = useState(false);
    const [letterPickerResetKey, setLetterPickerResetKey] = useState(0);
    const { addErrors: addPhonicsErrors } = usePhonicsErrorCollector(undefined, 'placement_test');
    const { handleLetterError, getSessionErrors } = usePhonicsGuidance();
    // Track LetterPicker errors/hints so we can penalize revealed letters in scoring
    const lpErrorDataRef = useRef<import('../components/learning/LetterPicker').LetterPickerErrorData | null>(null);
    const handleLpErrorData = useCallback((data: import('../components/learning/LetterPicker').LetterPickerErrorData) => {
        lpErrorDataRef.current = data;
        addPhonicsErrors(data);
    }, [addPhonicsErrors]);

    // Find the first inputable word index (skip punctuation-only tokens)
    const findFirstInputableIndex = (question: DictationQuestion): number => {
        const punct = question.wordPunctuation || [];
        for (let i = 0; i < question.wordCount; i++) {
            if (!punct[i]?.isPunctuationOnly) return i;
        }
        return 0;
    };

    // Focus the active dictation word input
    useEffect(() => {
        if (phase === 'listening-dictation' && dictationInputRefs.current[activeDictationWordIndex]) {
            dictationInputRefs.current[activeDictationWordIndex]?.focus();
        }
    }, [activeDictationWordIndex, phase]);

    // Fetch question breakdown when result phase is entered (inline result from submitAnswer lacks it)
    useEffect(() => {
        if (phase === 'result' && sessionId && result && !result.questionBreakdown && isAuthenticated) {
            placementTestService.getResult(sessionId).then((data) => {
                if (data.questionBreakdown) {
                    setResult(prev => prev ? { ...prev, questionBreakdown: data.questionBreakdown } : prev);
                }
            }).catch(() => { /* breakdown is optional, ignore errors */ });
        }
    }, [phase, sessionId, result, isAuthenticated]);

    // Check if returning from login (URL param or sessionStorage for OAuth)
    useEffect(() => {
        const pendingSession = searchParams.get('session') || sessionStorage.getItem('placementTestSession');
        if (pendingSession && isAuthenticated && hasToken()) {
            // Clear sessionStorage after use
            sessionStorage.removeItem('placementTestSession');
            // User logged in after completing anonymous test — redirect to result page
            navigate(`/placement-test/result/${pendingSession}`, { replace: true });
        }
    }, [searchParams, isAuthenticated, navigate]);

    // Start test
    const handleStartTest = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await placementTestService.startTest();
            setSessionId(response.sessionId);
            setBackgroundQuestions(response.backgroundQuestions);
            setPhase('background');
        } catch (err) {
            setError(t('errors.startFailed'));
            console.error('Failed to start test:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle background question answer (single-select)
    const handleBackgroundAnswer = (optionIndex: number) => {
        const question = backgroundQuestions[currentBgIndex];
        const answer: BackgroundAnswer = {
            questionId: question.id,
            optionIndex,
        };

        const newAnswers = [...backgroundAnswers, answer];
        setBackgroundAnswers(newAnswers);

        if (currentBgIndex < backgroundQuestions.length - 1) {
            setCurrentBgIndex(currentBgIndex + 1);
            setMultiSelectChoices(new Set());
        } else {
            submitBackgroundAnswers(newAnswers);
        }
    };

    // Toggle a multi-select option
    const handleMultiSelectToggle = (optionIndex: number) => {
        setMultiSelectChoices(prev => {
            const next = new Set(prev);
            if (next.has(optionIndex)) {
                next.delete(optionIndex);
            } else {
                next.add(optionIndex);
            }
            return next;
        });
    };

    // Confirm multi-select answer
    const handleMultiSelectConfirm = () => {
        const question = backgroundQuestions[currentBgIndex];
        const indices = Array.from(multiSelectChoices).sort((a, b) => a - b);
        const selectedCodes = indices
            .map(i => question.options[i]?.code)
            .filter((c): c is string => !!c);

        const answer: BackgroundAnswer = {
            questionId: question.id,
            optionIndex: indices[0] ?? 0,
            optionIndices: indices,
            selectedTopicCodes: selectedCodes,
        };

        const newAnswers = [...backgroundAnswers, answer];
        setBackgroundAnswers(newAnswers);

        if (currentBgIndex < backgroundQuestions.length - 1) {
            setCurrentBgIndex(currentBgIndex + 1);
            setMultiSelectChoices(new Set());
        } else {
            submitBackgroundAnswers(newAnswers);
        }
    };

    // Submit background answers
    const submitBackgroundAnswers = async (answers: BackgroundAnswer[]) => {
        if (!sessionId) return;

        setIsLoading(true);
        try {
            const response = await placementTestService.submitBackground(sessionId, answers);
            setProbabilities(response.priorProbabilities);
            setCurrentQuestion(response.firstQuestion);
            setQuestionIndex(1);
            setQuestionStartTime(Date.now());
            setPhase('testing');
        } catch (err) {
            setError(t('errors.submitFailed'));
            console.error('Failed to submit background:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle answer submission (regular answer or skip)
    const submitAnswerOrSkip = async (skipped: boolean) => {
        if (!sessionId || !currentQuestion || isSubmitting) return;
        if (!skipped && selectedOption === null) return;

        setIsSubmitting(true);
        const responseTimeMs = Date.now() - questionStartTime;

        try {
            const response = await placementTestService.submitAnswer(
                sessionId,
                currentQuestion.id,
                skipped ? -1 : selectedOption!,
                responseTimeMs,
                skipped,
            );

            setLastAnswerCorrect(skipped ? false : response.isCorrect);
            setProbabilities(response.currentProbabilities);
            setQuestionIndex(response.questionIndex);

            // Brief delay to show correct/incorrect feedback
            await new Promise(resolve => setTimeout(resolve, 500));

            if (response.nextQuestion) {
                // Continue with next question
                setCurrentQuestion(response.nextQuestion);
                setSelectedOption(null);
                setLastAnswerCorrect(null);
                setQuestionStartTime(Date.now());
            } else if (response.phaseComplete === 'core' && response.nextPhase === 'listening_dictation') {
                // Core test complete, transition to listening dictation
                await loadListeningDictation();
            } else if (response.requiresLogin) {
                // Anonymous user completed - need to login
                setPreview(response.preview || null);
                setPhase('login-prompt');
            } else if (response.result) {
                // Authenticated user completed - show result
                setResult(response.result);
                setPhase('result');
            }
        } catch (err) {
            setError(t('errors.submitFailed'));
            console.error('Failed to submit answer:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAnswerSubmit = () => submitAnswerOrSkip(false);
    const handleSkip = () => submitAnswerOrSkip(true);

    // ============================================
    // LISTENING DICTATION HANDLERS
    // ============================================

    const loadListeningDictation = async (mode?: DictationInputMode) => {
        if (!sessionId) return;
        const effectiveMode = mode || dictationInputMode || 'keyboard';
        setIsLoading(true);
        try {
            const data = await placementTestService.getListeningDictation(
                sessionId,
                effectiveMode === 'letter-picker' ? 'letter-picker' : 'keyboard',
            );
            if (data.questions.length === 0) {
                // No dictation questions available, skip to speaking
                await loadSpeakingQuestions();
                return;
            }
            setDictationQuestions(data.questions);
            setCurrentDictationIndex(0);
            // Initialize word inputs from wordCount
            const firstQ = data.questions[0];
            const wordCount = firstQ?.wordCount || 0;
            setDictationWordInputs(new Array(wordCount).fill(''));
            setActiveDictationWordIndex(firstQ ? findFirstInputableIndex(firstQ) : 0);
            dictationInputRefs.current = [];
            setDictationStartTime(Date.now());
            setLastDictationScore(null);
            setLetterPickerResetKey((k) => k + 1);
            setPhase('listening-dictation');

            // Show input mode modal on first entry if user hasn't chosen yet
            if (!localStorage.getItem('pt-dictation-input-mode')) {
                setShowInputModeModal(true);
            }
        } catch (err) {
            // If listening dictation fails (e.g. no questions), skip to speaking
            try {
                await loadSpeakingQuestions();
            } catch {
                setError(t('errors.submitFailed'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Handle input mode selection from modal
    const handleInputModeSelect = async (mode: DictationInputMode) => {
        setDictationInputMode(mode);
        localStorage.setItem('pt-dictation-input-mode', mode);
        setShowInputModeModal(false);

        // Show tooltip pointing to preference button
        if (!localStorage.getItem('pt-input-mode-tooltip-shown')) {
            localStorage.setItem('pt-input-mode-tooltip-shown', '1');
            setShowPreferenceTooltip(true);
        }

        // If switching to letter-picker, reload questions with expectedText
        if (mode === 'letter-picker' && sessionId) {
            const data = await placementTestService.getListeningDictation(sessionId, 'letter-picker');
            if (data.questions.length > 0) {
                setDictationQuestions(data.questions);
            }
        }
    };

    // Handle preference button click (toggle mode)
    const handleToggleInputMode = async () => {
        const newMode: DictationInputMode = dictationInputMode === 'letter-picker' ? 'keyboard' : 'letter-picker';
        setDictationInputMode(newMode);
        localStorage.setItem('pt-dictation-input-mode', newMode);

        // Reload questions if switching to letter-picker (need expectedText)
        if (newMode === 'letter-picker' && sessionId) {
            const data = await placementTestService.getListeningDictation(sessionId, 'letter-picker');
            if (data.questions.length > 0) {
                setDictationQuestions(data.questions);
                setLetterPickerResetKey((k) => k + 1);
            }
        }
    };

    // Get recommended input mode based on estimated CEFR level
    const getRecommendedInputMode = (): DictationInputMode => {
        const firstQ = dictationQuestions[0];
        if (!firstQ) return 'keyboard';
        const level = firstQ.cefrLevel;
        return (level === 'A1' || level === 'A2') ? 'letter-picker' : 'keyboard';
    };

    const handleDictationSubmit = async () => {
        if (!sessionId || isSubmitting) return;
        const question = dictationQuestions[currentDictationIndex];
        if (!question) return;

        // Join word inputs back into a text string for API submission
        const userText = dictationWordInputs.join(' ').trim();

        setIsSubmitting(true);
        const responseTimeMs = Date.now() - dictationStartTime;

        try {
            const response = await placementTestService.submitDictation(
                sessionId,
                question.id,
                userText,
                responseTimeMs,
            );

            setLastDictationScore(response.score);

            // Brief delay to show score
            await new Promise(resolve => setTimeout(resolve, 1500));

            if (response.isComplete) {
                // Load speaking questions
                await loadSpeakingQuestions();
            } else {
                // Next dictation question
                const nextQuestion = dictationQuestions[response.questionIndex];
                setCurrentDictationIndex(response.questionIndex);
                const wordCount = nextQuestion?.wordCount || 0;
                setDictationWordInputs(new Array(wordCount).fill(''));
                setActiveDictationWordIndex(nextQuestion ? findFirstInputableIndex(nextQuestion) : 0);
                dictationInputRefs.current = [];
                setDictationStartTime(Date.now());
                setLastDictationScore(null);
            }
        } catch (err) {
            setError(t('errors.submitFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle word input change for dictation
    const handleDictationWordChange = (index: number, value: string) => {
        const question = dictationQuestions[currentDictationIndex];
        if (!question) return;

        // Detect full-width characters and warn user
        if (containsFullWidthAlpha(value)) {
            const now = Date.now();
            if (now - fullWidthWarningRef.current > 3000) {
                fullWidthWarningRef.current = now;
                useToastStore.getState().warning(t('listening.fullWidthWarning'), 3000);
            }
            value = toHalfWidth(value);
        }

        // No spaces in individual word inputs
        const cleanValue = value.replace(/\s/g, '');
        const maxLen = question.wordLengths[index] || 20;
        const truncatedValue = cleanValue.slice(0, maxLen);

        const newInputs = [...dictationWordInputs];
        newInputs[index] = truncatedValue;
        setDictationWordInputs(newInputs);

        // Auto-advance when reaching max length (skip punctuation-only tokens)
        if (truncatedValue.length >= maxLen && index < question.wordCount - 1) {
            const wordPunctuation = question.wordPunctuation || [];
            setTimeout(() => {
                let nextIdx = index + 1;
                while (nextIdx < question.wordCount && wordPunctuation[nextIdx]?.isPunctuationOnly) {
                    nextIdx++;
                }
                if (nextIdx < question.wordCount) {
                    setActiveDictationWordIndex(nextIdx);
                }
            }, 100);
        }
    };

    // Handle keyboard events for dictation word inputs
    const handleDictationKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        const question = dictationQuestions[currentDictationIndex];
        if (!question) return;
        const input = dictationWordInputs[index];
        const wordPunctuation = question.wordPunctuation || [];

        // Space = toggle audio play/pause
        if (e.key === ' ') {
            e.preventDefault();
            dictationAudioRef.current?.togglePlayPause();
        }

        // Backspace at empty = go to previous word (skip punctuation-only)
        if (e.key === 'Backspace' && input === '' && index > 0) {
            e.preventDefault();
            let prevIdx = index - 1;
            while (prevIdx >= 0 && wordPunctuation[prevIdx]?.isPunctuationOnly) {
                prevIdx--;
            }
            if (prevIdx >= 0) setActiveDictationWordIndex(prevIdx);
        }

        // Enter = submit
        if (e.key === 'Enter') {
            e.preventDefault();
            handleDictationSubmit();
        }

        // Tab = move between words (skip punctuation-only)
        if (e.key === 'Tab') {
            e.preventDefault();
            if (e.shiftKey) {
                let prevIdx = index - 1;
                while (prevIdx >= 0 && wordPunctuation[prevIdx]?.isPunctuationOnly) {
                    prevIdx--;
                }
                if (prevIdx >= 0) setActiveDictationWordIndex(prevIdx);
            } else {
                let nextIdx = index + 1;
                while (nextIdx < question.wordCount && wordPunctuation[nextIdx]?.isPunctuationOnly) {
                    nextIdx++;
                }
                if (nextIdx < question.wordCount) setActiveDictationWordIndex(nextIdx);
            }
        }
    };

    // ============================================
    // SPEAKING HANDLERS
    // ============================================

    const loadSpeakingQuestions = async () => {
        if (!sessionId) return;
        setIsLoading(true);
        try {
            const data = await placementTestService.getSpeakingQuestion(sessionId);
            if (data.questions.length === 0) {
                // No speaking questions available, skip to final result
                await handleSkipSpeaking();
                return;
            }
            setSpeakingQuestions(data.questions);
            setCurrentSpeakingIndex(0);
            setLastSpeakingScore(null);
            setPhase('speaking');
        } catch (err) {
            // If speaking fails, try to skip and finalize
            try {
                await handleSkipSpeaking();
            } catch {
                setError(t('errors.submitFailed'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Show recording errors as page errors
    useEffect(() => {
        if (recordingError) setError(recordingError);
    }, [recordingError]);

    const handleSpeakingSubmit = async () => {
        if (!sessionId || isSubmitting || !speakingAudioBlob) return;
        const question = speakingQuestions[currentSpeakingIndex];
        if (!question) return;

        setIsSubmitting(true);
        try {
            const response = await placementTestService.submitSpeaking(
                sessionId,
                question.id,
                speakingAudioBlob,
            );

            setLastSpeakingScore(response.score);

            // Brief delay to show score
            await new Promise(resolve => setTimeout(resolve, 1500));

            if (response.isComplete) {
                await loadFinalResult();
            } else {
                setCurrentSpeakingIndex(response.questionIndex);
                clearSpeakingRecording();
                setLastSpeakingScore(null);
            }
        } catch (err) {
            setError(t('errors.submitFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSkipSpeaking = async () => {
        if (!sessionId) return;
        setIsLoading(true);
        try {
            await placementTestService.skipSpeaking(sessionId);
            await loadFinalResult();
        } catch (err) {
            setError(t('errors.submitFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const loadFinalResult = async () => {
        if (!sessionId) return;

        // If not authenticated, show login prompt
        if (!hasToken()) {
            setPreview({
                message: t('loginPrompt.title'),
                teaser: t('listening.testComplete'),
            });
            setPhase('login-prompt');
            return;
        }

        // Navigate to standalone result page
        navigate(`/placement-test/result/${sessionId}`, { replace: true });
    };

    // Handle login redirect
    const handleLoginRedirect = () => {
        // Save session ID to sessionStorage for OAuth flow (OAuth loses URL params)
        if (sessionId) {
            sessionStorage.setItem('placementTestSession', sessionId);
        }
        // Save redirect URL and navigate to login
        const redirectUrl = encodeURIComponent(`/placement-test?session=${sessionId}`);
        navigate(`/login?redirect=${redirectUrl}`);
    };

    // Restart test
    const handleRestartTest = () => {
        setPhase('intro');
        setSessionId(null);
        setBackgroundQuestions([]);
        setBackgroundAnswers([]);
        setCurrentBgIndex(0);
        setCurrentQuestion(null);
        setQuestionIndex(0);
        setProbabilities(null);
        setSelectedOption(null);
        setResult(null);
        setPreview(null);
        setError(null);
    };

    // Get CEFR level color
    const getLevelColor = (level: CEFRLevel): string => {
        const colors: Record<CEFRLevel, string> = {
            A1: '#22c55e',
            A2: '#84cc16',
            B1: '#eab308',
            B2: '#f97316',
            C1: '#ef4444',
            C2: '#dc2626',
        };
        return colors[level];
    };

    // Render intro phase
    const renderIntro = () => (
        <div className="placement-test-intro">
            <h1>
                <span className="cefr-with-help">
                    CEFR
                    <button
                        className="cefr-help-btn"
                        onClick={() => setShowCefrModal(true)}
                        aria-label={t('cefrModal.title')}
                    >
                        ?
                    </button>
                </span>
                {' '}{t('intro.titleSuffix')}
            </h1>
            <p className="intro-description">{t('intro.description')}</p>
            <p className="intro-reassurance">{t('intro.reassurance')}</p>

            <p className="intro-meta">10 {t('intro.questionsCount')} · 5-10 {t('intro.minutes')}</p>

            <div className="intro-stepper">
                <span className="stepper-step">{t('intro.steps.background')}</span>
                <span className="stepper-arrow">→</span>
                <span className="stepper-step">{t('intro.steps.core')}</span>
                <span className="stepper-arrow">→</span>
                <span className="stepper-step">{t('intro.steps.listening')}</span>
                <span className="stepper-arrow">→</span>
                <span className="stepper-step">{t('intro.steps.speaking')}</span>
            </div>

            <p className="intro-difficulty-note">{t('intro.difficultyNote')}</p>

            <button
                className="start-test-btn"
                onClick={handleStartTest}
                disabled={isLoading}
            >
                {isLoading ? t('loading') : t('intro.startButton')}
            </button>

            {isAuthenticated && (
                <button
                    className="intro-history-link"
                    onClick={() => navigate('/placement-test/history')}
                >
                    {t('intro.viewHistory')}
                </button>
            )}
        </div>
    );

    // Render background questions phase
    const renderBackground = () => {
        const question = backgroundQuestions[currentBgIndex];
        if (!question) return null;

        const isMultiSelect = question.type === 'multi-select';
        const minSelections = question.minSelections || 1;

        return (
            <div className="placement-test-background">
                <div className="progress-bar">
                    <div
                        className="progress-fill"
                        style={{ width: `${((currentBgIndex) / backgroundQuestions.length) * 100}%` }}
                    />
                </div>
                <p className="progress-text">
                    {t('background.progress', { current: currentBgIndex + 1, total: backgroundQuestions.length })}
                </p>

                <div className="question-card">
                    <h2 className="question-text">
                        {localized(question.question)}
                    </h2>

                    {isMultiSelect ? (
                        <>
                            <div className="topic-chips-grid">
                                {question.options.map((option, index) => {
                                    const selected = multiSelectChoices.has(index);
                                    return (
                                        <button
                                            key={option.code || index}
                                            className={`topic-chip${selected ? ' selected' : ''}`}
                                            onClick={() => handleMultiSelectToggle(index)}
                                            disabled={isLoading}
                                        >
                                            {option.icon && <span className="topic-chip-icon">{option.icon}</span>}
                                            <span className="topic-chip-label">{localized(option.text)}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="multi-select-footer">
                                <span className="selection-count">
                                    {t('background.selected', { count: multiSelectChoices.size, min: minSelections })}
                                </span>
                                <button
                                    className="continue-btn"
                                    onClick={handleMultiSelectConfirm}
                                    disabled={multiSelectChoices.size < minSelections || isLoading}
                                >
                                    {t('background.continue')}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="options-list">
                            {question.options.map((option, index) => (
                                <button
                                    key={index}
                                    className="option-btn"
                                    onClick={() => handleBackgroundAnswer(index)}
                                    disabled={isLoading}
                                >
                                    {localized(option.text)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render testing phase
    const renderTesting = () => {
        if (!currentQuestion) return null;

        return (
            <div className="placement-test-testing">
                <div className="test-header">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${(questionIndex / 10) * 100}%` }}
                        />
                    </div>
                    <p className="progress-text">
                        {t('testing.question', { current: questionIndex, total: 10 })}
                    </p>
                    <p className="testing-adaptive-note">{t('testing.adaptiveNote')}</p>
                </div>

                {/* Probability distribution - hidden during test, only shown in results */}

                <div className={`question-card ${lastAnswerCorrect !== null ? (lastAnswerCorrect ? 'correct' : 'incorrect') : ''}`}>
                    <p className="question-type-badge">
                        {t(`questionTypes.${currentQuestion.questionType}`)}
                    </p>
                    <h2 className="question-stem">{currentQuestion.content.stem}</h2>

                    <div className="options-grid">
                        {currentQuestion.content.options.map((option) => (
                            <button
                                key={option.id}
                                className={`test-option-btn ${selectedOption === option.id ? 'selected' : ''}`}
                                onClick={() => setSelectedOption(option.id)}
                                disabled={isSubmitting}
                            >
                                <span className="option-letter">
                                    {String.fromCharCode(65 + option.id)}
                                </span>
                                <span className="option-text">{option.text}</span>
                            </button>
                        ))}
                    </div>

                    <div className="answer-actions">
                        <button
                            className="submit-answer-btn"
                            onClick={handleAnswerSubmit}
                            disabled={selectedOption === null || isSubmitting}
                        >
                            {isSubmitting ? t('submitting') : t('testing.submitAnswer')}
                        </button>
                        <button
                            className="skip-answer-btn"
                            onClick={handleSkip}
                            disabled={isSubmitting}
                        >
                            {t('testing.skip')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Handle letter picker completion
    const handleLetterPickerComplete = (userText: string) => {
        // Replace revealed (hint/auto-reveal) letters with '_' so backend scorer
        // correctly marks those words as incorrect instead of giving full credit
        let textToSubmit = userText;
        const errData = lpErrorDataRef.current;
        if (errData && errData.revealedPositions.length > 0) {
            const chars = userText.split('');
            for (const pos of errData.revealedPositions) {
                if (pos < chars.length) {
                    chars[pos] = '_';
                }
            }
            textToSubmit = chars.join('');
        }
        lpErrorDataRef.current = null;

        // Auto-submit after a brief delay to show the completed sentence
        setTimeout(() => {
            handleDictationSubmitText(textToSubmit);
        }, 500);
    };

    // Handle letter picker skip — submit empty text so backend scores 0 and advances
    const handleLetterPickerSkip = () => {
        handleDictationSubmitText('');
    };

    // Submit dictation with explicit text (used by letter picker)
    const handleDictationSubmitText = async (text: string) => {
        if (!sessionId || isSubmitting) return;
        const question = dictationQuestions[currentDictationIndex];
        if (!question) return;

        setIsSubmitting(true);
        try {
            const responseTimeMs = Date.now() - dictationStartTime;
            const response = await placementTestService.submitDictation(
                sessionId,
                question.id,
                text,
                responseTimeMs,
            );

            setLastDictationScore(response.score);

            if (response.isComplete) {
                if (response.nextPhase === 'speaking') {
                    setTimeout(() => loadSpeakingQuestions(), 1500);
                }
            } else {
                setTimeout(() => {
                    const nextQuestion = dictationQuestions[response.questionIndex];
                    setCurrentDictationIndex(response.questionIndex);
                    if (nextQuestion) {
                        const wordCount = nextQuestion.wordCount || 0;
                        setDictationWordInputs(new Array(wordCount).fill(''));
                        setActiveDictationWordIndex(findFirstInputableIndex(nextQuestion));
                        dictationInputRefs.current = [];
                        setLetterPickerResetKey((k) => k + 1);
                    }
                    setDictationStartTime(Date.now());
                    setLastDictationScore(null);
                }, 1500);
            }
        } catch (err) {
            setError(t('errors.submitFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render listening dictation phase
    const renderListeningDictation = () => {
        const question = dictationQuestions[currentDictationIndex];
        if (!question) return null;

        const hasInput = dictationWordInputs.some(w => w.trim() !== '');
        const wordLengths = question.wordLengths || [];
        const wordPunctuation = question.wordPunctuation || [];
        const isLetterPickerMode = dictationInputMode === 'letter-picker';

        return (
            <div className="placement-test-dictation">
                <div className="test-header">
                    <div className="test-header-top">
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${((currentDictationIndex + 1) / dictationQuestions.length) * 100}%` }}
                            />
                        </div>
                        {/* Preference button for switching input mode */}
                        <button
                            className="pt-preference-btn"
                            onClick={handleToggleInputMode}
                            title={t('inputMode.switchTooltip')}
                        >
                            {isLetterPickerMode ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="6" width="20" height="12" rx="2" />
                                    <line x1="6" y1="10" x2="6" y2="10" /><line x1="10" y1="10" x2="10" y2="10" /><line x1="14" y1="10" x2="14" y2="10" /><line x1="18" y1="10" x2="18" y2="10" />
                                    <line x1="8" y1="14" x2="16" y2="14" />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="7" rx="1.5" />
                                    <rect x="3" y="13" width="7" height="7" rx="1.5" /><rect x="14" y="13" width="7" height="7" rx="1.5" />
                                    <text x="6.5" y="9.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">a</text>
                                    <text x="17.5" y="9.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">b</text>
                                    <text x="6.5" y="18.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">c</text>
                                    <text x="17.5" y="18.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">d</text>
                                </svg>
                            )}
                        </button>
                    </div>
                    <p className="progress-text">
                        {t('listening.progress', { current: currentDictationIndex + 1, total: dictationQuestions.length })}
                    </p>
                </div>

                <div className="question-card">
                    <p className="question-type-badge">{t('listening.title')}</p>
                    <p className="dictation-instruction">{t('listening.instruction')}</p>

                    <div className="dictation-audio">
                        <AudioPlayer
                            ref={dictationAudioRef}
                            audioUrl={question.audioUrl}
                        />
                    </div>

                    {question.hints && question.hints.length > 0 && (
                        <div className="dictation-hints">
                            <span className="hints-label">{t('listening.hints')}:</span>
                            {question.hints.map((h, i) => (
                                <span key={i} className="hint-word">{h}</span>
                            ))}
                        </div>
                    )}

                    {/* Letter Picker Mode */}
                    {isLetterPickerMode && question.expectedText ? (
                        <div className="dictation-letter-picker-area">
                            <LetterPicker
                                expectedText={question.expectedText}
                                onComplete={handleLetterPickerComplete}
                                onErrorData={handleLpErrorData}
                                onLetterError={handleLetterError}
                                onSkip={handleLetterPickerSkip}
                                skipLabel={t('listening.skip')}
                                disabled={isSubmitting || !!lastDictationScore}
                                resetKey={letterPickerResetKey}
                            />
                        </div>
                    ) : (
                        /* Native Keyboard Mode */
                        <div className="dictation-word-inputs">
                            {Array.from({ length: question.wordCount }, (_, idx) => {
                                const punct = wordPunctuation[idx];

                                // Standalone punctuation token (e.g., "—", "…") — render as static text
                                if (punct?.isPunctuationOnly) {
                                    const rawText = [punct.before, punct.after].filter(Boolean).join('') || '—';
                                    return (
                                        <span key={idx} className="pt-word-punctuation-display">{rawText}</span>
                                    );
                                }

                                const userInput = dictationWordInputs[idx] || '';
                                const isActive = idx === activeDictationWordIndex;
                                const showingResult = !!lastDictationScore;

                                const charWidth = 14;
                                const padding = 32;
                                const wordLen = wordLengths[idx] || 5;
                                const minWidth = Math.max(wordLen * charWidth + padding, 60);

                                const inputClassName = `pt-word-input ${isActive ? 'pt-word-input--active' : ''}`;

                                const beforePunct = punct?.before || '';
                                const afterPunct = punct?.after || '';

                                return (
                                    <div key={idx} className="pt-word-input-wrapper">
                                        {beforePunct && <span className="pt-word-punctuation pt-word-punctuation--leading">{beforePunct}</span>}
                                        <input
                                            ref={(el) => { dictationInputRefs.current[idx] = el; }}
                                            type="text"
                                            className={inputClassName}
                                            value={userInput}
                                            onChange={(e) => handleDictationWordChange(idx, e.target.value)}
                                            onKeyDown={(e) => handleDictationKeyDown(idx, e)}
                                            onFocus={() => setActiveDictationWordIndex(idx)}
                                            disabled={isSubmitting || showingResult}
                                            style={{ width: `${minWidth}px` }}
                                            maxLength={wordLen}
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="none"
                                            inputMode="text"
                                            spellCheck={false}
                                        />
                                        {afterPunct && <span className="pt-word-punctuation">{afterPunct}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {lastDictationScore && (
                        <div className="dictation-score">
                            <span className="score-value">{Math.round(lastDictationScore.score * 100)}%</span>
                            <span className="score-detail">
                                {lastDictationScore.correctWords}/{lastDictationScore.totalWords} {t('listening.wordsCorrect')}
                            </span>
                        </div>
                    )}

                    {/* Only show submit button in keyboard mode */}
                    {!isLetterPickerMode && (
                        <button
                            className="submit-answer-btn"
                            onClick={handleDictationSubmit}
                            disabled={!hasInput || isSubmitting}
                        >
                            {isSubmitting ? t('submitting') : t('listening.submit')}
                        </button>
                    )}
                </div>

                {/* Input Mode Selection Modal */}
                {showInputModeModal && (
                    <InputModeModal
                        recommended={getRecommendedInputMode()}
                        cefrLevel={question.cefrLevel}
                        onSelect={handleInputModeSelect}
                    />
                )}

                {/* Preference Tooltip */}
                <PreferenceTooltip
                    visible={showPreferenceTooltip}
                    onDismiss={() => setShowPreferenceTooltip(false)}
                />
            </div>
        );
    };

    // Render speaking phase
    const renderSpeaking = () => {
        const question = speakingQuestions[currentSpeakingIndex];
        if (!question) return null;

        return (
            <div className="placement-test-speaking">
                <div className="test-header">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${((currentSpeakingIndex + 1) / speakingQuestions.length) * 100}%` }}
                        />
                    </div>
                    <p className="progress-text">
                        {t('speaking.progress', { current: currentSpeakingIndex + 1, total: speakingQuestions.length })}
                    </p>
                </div>

                <div className="question-card">
                    <p className="question-type-badge">{t('speaking.title')}</p>
                    <p className="speaking-instruction">{t('speaking.instruction')}</p>

                    <div className="speaking-reference">
                        <p className="reference-text">{question.referenceText}</p>
                        <AudioPlayer audioUrl={question.audioUrl} />
                    </div>

                    <div className="speaking-controls">
                        {!isRecording ? (
                            <button
                                className="record-btn"
                                onClick={startRecording}
                                disabled={isSubmitting}
                            >
                                {t('speaking.record')}
                            </button>
                        ) : (
                            <button
                                className="record-btn record-btn--recording"
                                onClick={stopRecording}
                            >
                                {t('speaking.stop')}
                            </button>
                        )}
                    </div>

                    {lastSpeakingScore && (
                        <div className="speaking-score">
                            <span className="score-value">{Math.round(lastSpeakingScore.score * 100)}%</span>
                            <div className="score-breakdown">
                                <span>{t('speaking.accuracy')}: {lastSpeakingScore.accuracy}</span>
                                <span>{t('speaking.fluency')}: {lastSpeakingScore.fluency}</span>
                            </div>
                        </div>
                    )}

                    <button
                        className="submit-answer-btn"
                        onClick={handleSpeakingSubmit}
                        disabled={!speakingAudioBlob || isSubmitting || isRecording}
                    >
                        {isSubmitting ? t('submitting') : t('speaking.submit')}
                    </button>

                    <button
                        className="skip-speaking-btn"
                        onClick={handleSkipSpeaking}
                        disabled={isSubmitting}
                    >
                        {t('speaking.skip')}
                    </button>
                </div>
            </div>
        );
    };

    // Render login prompt phase
    const renderLoginPrompt = () => (
        <div className="placement-test-login-prompt">
            <div className="prompt-icon">
                <svg viewBox="0 0 24 24" width="80" height="80" fill="none" stroke="var(--color-primary)" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16,17 21,12 16,7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
            </div>

            <h1>{preview?.message || t('loginPrompt.title')}</h1>
            <p className="prompt-teaser">{preview?.teaser}</p>

            <div className="prompt-benefits">
                <div className="benefit">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12" />
                    </svg>
                    <span>{t('loginPrompt.benefit1')}</span>
                </div>
                <div className="benefit">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12" />
                    </svg>
                    <span>{t('loginPrompt.benefit2')}</span>
                </div>
                <div className="benefit">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12" />
                    </svg>
                    <span>{t('loginPrompt.benefit3')}</span>
                </div>
            </div>

            <button className="login-btn" onClick={handleLoginRedirect}>
                {t('loginPrompt.loginButton')}
            </button>

            <button className="register-link" onClick={() => {
                // Save session ID to sessionStorage for OAuth flow
                if (sessionId) sessionStorage.setItem('placementTestSession', sessionId);
                navigate(`/register?redirect=${encodeURIComponent(`/placement-test?session=${sessionId}`)}`);
            }}>
                {t('loginPrompt.registerLink')}
            </button>
        </div>
    );

    // Score to CEFR level mapping
    const scoreToCefr = (score: number): CEFRLevel => {
        if (score >= 0.875) return 'C2';
        if (score >= 0.725) return 'C1';
        if (score >= 0.575) return 'B2';
        if (score >= 0.40) return 'B1';
        if (score >= 0.225) return 'A2';
        return 'A1';
    };

    // Mini radar chart for dimensions (SVG)
    const DimensionRadar: React.FC<{ scores: Record<string, number> }> = ({ scores }) => {
        const dims = ['reading', 'grammar', 'vocabulary', 'listening', 'speaking'] as const;
        const size = 220;
        const cx = size / 2;
        const cy = size / 2;
        const r = size / 2 - 30;
        const numAxes = dims.length;
        const angleStep = (2 * Math.PI) / numAxes;

        const getPoint = (i: number, val: number) => {
            const angle = angleStep * i - Math.PI / 2;
            return { x: cx + r * val * Math.cos(angle), y: cy + r * val * Math.sin(angle) };
        };

        const rings = [0.25, 0.5, 0.75, 1.0];
        const activeEntries = dims.map((d, i) => ({ dim: d, val: scores[d], i })).filter(e => e.val != null);

        let dataPath = '';
        if (activeEntries.length >= 3) {
            const pts = activeEntries.map(e => getPoint(e.i, e.val));
            dataPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
        }

        return (
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="dimension-radar-svg">
                {rings.map((rv, ri) => {
                    const pts = dims.map((_, i) => getPoint(i, rv));
                    const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
                    return <path key={ri} d={path} fill="none" stroke="#e2e8f0" strokeWidth={rv === 1 ? 1 : 0.5} opacity={0.6} />;
                })}
                {dims.map((_, i) => {
                    const ep = getPoint(i, 1);
                    return <line key={i} x1={cx} y1={cy} x2={ep.x} y2={ep.y} stroke="#e2e8f0" strokeWidth={0.5} opacity={0.5} />;
                })}
                {dataPath && <path d={dataPath} fill="rgba(102,126,234,0.15)" stroke="#667eea" strokeWidth="2" />}
                {activeEntries.map(e => {
                    const p = getPoint(e.i, e.val);
                    return <circle key={e.i} cx={p.x} cy={p.y} r={4} fill={getLevelColor(scoreToCefr(e.val))} stroke="#fff" strokeWidth={1.5} />;
                })}
                {dims.map((d, i) => {
                    const angle = angleStep * i - Math.PI / 2;
                    const lr = r + 22;
                    const lx = cx + lr * Math.cos(angle);
                    const ly = cy + lr * Math.sin(angle);
                    const anchor = Math.abs(Math.cos(angle)) < 0.1 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
                    const hasScore = scores[d] != null;
                    return (
                        <text key={i} x={lx} y={ly} textAnchor={anchor} dominantBaseline="central"
                            fontSize={11} fontWeight={500} fill={hasScore ? '#334155' : '#94a3b8'}>
                            {t(`result.dimensions.${d}`)}
                        </text>
                    );
                })}
            </svg>
        );
    };

    // Render result phase
    const renderResult = () => {
        if (!result) return null;

        const dimScores = result.dimensionScores || {};
        const dims = ['reading', 'grammar', 'vocabulary', 'listening', 'speaking'] as const;
        const testedDims = dims.filter(d => dimScores[d] != null);

        // Find strongest and weakest (only label if scores actually differ)
        let strongest = '';
        let weakest = '';
        let maxScore = -1;
        let minScore = 2;
        for (const d of testedDims) {
            const s = dimScores[d]!;
            if (s > maxScore) { maxScore = s; strongest = d; }
            if (s < minScore) { minScore = s; weakest = d; }
        }
        if (maxScore === minScore) {
            strongest = '';
            weakest = '';
        }

        return (
            <div className="placement-test-result">
                <div className="result-header">
                    <h1>{t('result.title')}</h1>
                </div>

                <div className="result-main">
                    <div
                        className="level-badge"
                        style={{ backgroundColor: getLevelColor(result.compositeCefrLevel || result.primaryLevel) }}
                    >
                        <span className="level-text">{result.compositeCefrLevel || result.primaryLevel}</span>
                        <span className="level-name">{result.levelDescription.name}</span>
                    </div>

                    <p className="result-display-text">
                        {t('result.levelHeadline', {
                            level: result.compositeCefrLevel || result.primaryLevel,
                        })}
                    </p>

                </div>

                {/* Dimension Breakdown */}
                {testedDims.length > 0 && (
                    <div className="dimension-section">
                        <h3>{t('result.dimensionTitle')}</h3>
                        <div className="dimension-layout">
                            <div className="dimension-radar">
                                <DimensionRadar scores={dimScores} />
                            </div>
                            <div className="dimension-cards">
                                {dims.map(d => {
                                    const score = dimScores[d];
                                    const isTested = score != null;
                                    const cefr = isTested ? scoreToCefr(score) : null;
                                    const pct = isTested ? Math.round(score * 100) : 0;
                                    const isStrongest = d === strongest;
                                    const isWeakest = d === weakest && testedDims.length > 1;

                                    return (
                                        <div key={d} className={`dim-card ${!isTested ? 'dim-card--untested' : ''}`}>
                                            <div className="dim-card-header">
                                                <span className="dim-card-name">{t(`result.dimensions.${d}`)}</span>
                                                {isStrongest && <span className="dim-card-tag dim-card-tag--strong">{t('result.strongestSkill')}</span>}
                                                {isWeakest && <span className="dim-card-tag dim-card-tag--weak">{t('result.weakestSkill')}</span>}
                                            </div>
                                            {isTested ? (
                                                <>
                                                    <div className="dim-card-score">
                                                        <span className="dim-cefr" style={{ color: getLevelColor(cefr!) }}>{cefr}</span>
                                                        <div className="dim-bar-track">
                                                            <div className="dim-bar-fill" style={{ width: `${pct}%`, backgroundColor: getLevelColor(cefr!) }} />
                                                        </div>
                                                    </div>
                                                    {isWeakest && (
                                                        <p className="dim-recommendation">{t(`result.recommendations.${d}`)}</p>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="dim-not-tested">{t('result.dimensionNotTested')}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                <div className="result-details">
                    <div className="detail-item">
                        <span className="detail-label">{t('result.correctCount')}</span>
                        <span className="detail-value">{result.correctCount} / 10</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">{t('result.timeSpent')}</span>
                        <span className="detail-value">
                            {(() => {
                                const mins = Math.floor(result.totalTimeSeconds / 60);
                                const secs = result.totalTimeSeconds % 60;
                                return `${mins}:${String(secs).padStart(2, '0')}`;
                            })()}
                        </span>
                    </div>
                </div>

                <div className="level-description">
                    <h3>{t('result.whatThisMeans')}</h3>
                    <p>{result.levelDescription.description}</p>
                </div>

                {/* Distribution chart */}
                <div className="distribution-chart">
                    <h3>{t('result.distribution')}</h3>
                    <div className="distribution-bars">
                        {(Object.entries(result.fullDistribution) as [CEFRLevel, number][]).map(([level, prob]) => (
                            <div key={level} className="dist-bar-item">
                                <div className="dist-bar-wrapper">
                                    <div
                                        className="dist-bar"
                                        style={{
                                            height: `${prob * 100}%`,
                                            backgroundColor: getLevelColor(level),
                                        }}
                                    />
                                </div>
                                <span className="dist-label">{level}</span>
                                <span className="dist-percent">{Math.round(prob * 100)}%</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Question Breakdown */}
                {result.questionBreakdown && result.questionBreakdown.length > 0 && (
                    <QuestionBreakdownSection
                        breakdown={result.questionBreakdown}
                        showBreakdown={showBreakdown}
                        onToggle={() => setShowBreakdown(!showBreakdown)}
                        t={t}
                        locale={i18n.language}
                    />
                )}

                {/* Recommended Courses */}
                {sessionId && (
                    <RecommendedCoursesSection
                        sessionId={sessionId}
                        primaryLevel={result.compositeCefrLevel || result.primaryLevel}
                        t={t}
                        locale={i18n.language}
                    />
                )}

                <PhonicsSessionSummary errors={getSessionErrors()} />

                <div className="result-actions">
                    <button className="secondary-btn" onClick={handleRestartTest}>
                        {t('result.retakeTest')}
                    </button>
                    {isAuthenticated && (
                        <button className="secondary-btn" onClick={() => navigate('/placement-test/history')}>
                            {t('result.viewHistory')}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Render error
    if (error) {
        return (
            <div className="placement-test-page">
                <div className="error-container">
                    <h2>{t('errors.title')}</h2>
                    <p>{error}</p>
                    <button onClick={handleRestartTest}>{t('errors.retry')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="placement-test-page">
            {phase === 'intro' && renderIntro()}
            {phase === 'background' && renderBackground()}
            {phase === 'testing' && renderTesting()}
            {phase === 'listening-dictation' && renderListeningDictation()}
            {phase === 'speaking' && renderSpeaking()}
            {phase === 'login-prompt' && renderLoginPrompt()}
            {phase === 'result' && renderResult()}
            <CEFRGuideModal isOpen={showCefrModal} onClose={() => setShowCefrModal(false)} />
        </div>
    );
};

export default PlacementTestPage;
